import {
  createBrowserRouter,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import FullLayout from "../layouts/FullLayout/FullLayout";
import Login from "../pages/Login";
import {
  ADD_PROPERTY_ROUTE,
  EDIT_PROPERTY_ROUTE,
  LEADS_ROUTE,
  PROFILE_ROUTE,
  PROPERTY_ROUTE,
  ADD_LEAD_ROUTE,
  EDIT_LEAD_ROUTE,
  VIEW_ROUTE,
  APPOINTMENT_ROUTE,
  VIEWING_DONE_ROUTE,
  DEAL_DEAL_ROUTE,
  VIEW_DETAILS_ROUTE,
  HISTORY_ROUTE,
  CONTACTS_ROUTE,
  ACCOUNT_DEALS_ROUTE,
  ACCOUNT_ADD_TRANSACTION_ROUTE,
  TRANSACTION_DETAILS_ROUTE,
  TASKS_ROUTE,
  All_LEADS_ROUTE,
  LEADS_POOL_ROUTE,
  ADD_AGENT_ROUTE,
  TODAY_LEADS,
  TODAY_LEADS_ROUTE,
  INTEGRATION_ROUTE,
  AGENT_WHATSAPP_ROUTE,
} from "../constants/routes";
import { Suspense, lazy, useEffect, useMemo } from "react";
import RequireAuth from "../components/RequireAuth";
import { Box, CircularProgress } from "@mui/material";
import { selectCurrentAgent } from "../redux/features/authSlice";
import { useSelector } from "react-redux";
import { useGetAgencyPropertiesQuery } from "../redux/api/propertiesApi";
import { PropertiesData } from "../hooks/useContext/agencyProperties";
import { LeadsData } from "../hooks/useContext/leadsData";
import { useGetLeadsQuery } from "../redux/api/leadsApi";
import Contacts from "../pages/Contacts";
import { DealsPdf } from "../pages/Accounts/deals/Deals";
import { DealsDetails } from "../pages/Accounts/deals/DealsDetails";
import { AddTransaction } from "../pages/Accounts/deals/AddTransaction";
import { TransactionDetails } from "../pages/Accounts/deals/TransactionDetails";
import Tasks from "../pages/Tasks";
import Unauthorized from "../components/Unauthorized";
import { hasPermission } from "../helper/agentRights";
import AllLead from "../pages/Leads/AllLeads";
import LeadPool from "../pages/Leads/LeadPool";
import AddAgent from "../pages/Agents/AddAgent";
import TodayLeads from "../pages/Todayleads";
import Integration from "../pages/Integration";

const Dashboard = lazy(() => import("../pages/Dashboard"));
const Properties = lazy(() => import("../pages/Properties"));
const AddProperty = lazy(() => import("../pages/Properties/AddProperty"));
const EditProperty = lazy(() => import("../pages/Properties/EditProperty"));
const Profile = lazy(() => import("../pages/Profile"));
const Leads = lazy(() => import("../pages/Leads"));
const AddLead = lazy(() => import("../pages/Leads/AddLead"));
const EditLead = lazy(() => import("../pages/Leads/EditLead"));
const ViewingArranged = lazy(() =>
  import("../pages/Leads/status/ViewingArranged")
);
const Deal = lazy(() => import("../pages/Leads/status/deal/Deal"));
const ViewingDone = lazy(() => import("../pages/Leads/status/ViewingDone"));
const LeadDetails = lazy(() => import("../pages/Leads/LeadDetails"));
const History = lazy(() => import("../pages/Leads/History"));

// New component to check permissions
const RequirePermission = ({ children, requiredPermission }) => {
  const currentAgent = useSelector(selectCurrentAgent);
  const agentRights = currentAgent?.agentrights || {};

  return hasPermission(agentRights, requiredPermission) ? (
    children
  ) : (
    <Unauthorized />
  );
};

const SuspenseLayout = ({ children }) => {
  const DEFAULT_PAGE = 1;
  const DEFAULT_SIZE = 10;

  const DataProvider = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const currentAgent = useSelector(selectCurrentAgent);

    const isPropertiesRoute = location.pathname === "/properties";

    const currentTab = searchParams.get("tab"); // "myProperties" or "agencyProperties"
    const rawStatus = searchParams.get("status");
    const rawPage = searchParams.get("page");
    const rawSize = searchParams.get("size");

    // ✅ If tab is "agencyProperties", then fetch agency data; else fetch agent-only
    const scope = currentTab === "agencyProperties" ? "agency" : "agent";

    const propstat =
      rawStatus === null
        ? undefined
        : rawStatus === "all"
        ? "all"
        : Number(rawStatus);

    const pageNumber = rawPage ? Number(rawPage) : undefined;
    const pageSize = rawSize ? Number(rawSize) : undefined;

    // ✅ Reset pagination when scope or status changes
    useEffect(() => {
      const next = new URLSearchParams(searchParams);
      const current = Number(next.get("page") || DEFAULT_PAGE);
      if (current !== DEFAULT_PAGE) {
        next.set("page", String(DEFAULT_PAGE));
        setSearchParams(next, { replace: true });
      }
    }, [scope, rawStatus]);

    // ✅ API Query Args Logic
    const queryArgs = useMemo(() => {
      if (!isPropertiesRoute || !currentAgent) return undefined;

      const base = {
        ...(propstat !== undefined ? { propstat } : {}),
        ...(Number.isFinite(pageNumber) ? { pageNumber } : {}),
        ...(Number.isFinite(pageSize) ? { pageSize } : {}),
      };

    
      if (scope === "agent") {
        return { ...base, agentId: currentAgent.agentid };
      } else {
        return {
          ...base,
          agencyId: currentAgent.agencyid,
        };
      }
    }, [
      isPropertiesRoute,
      currentAgent,
      scope,
      propstat,
      pageNumber,
      pageSize,
    ]);

    // ✅ Skip query if missing IDs
    const shouldSkip =
      !isPropertiesRoute ||
      !currentAgent ||
      (scope === "agency"
        ? !currentAgent?.agencyid || !currentAgent?.agentid
        : !currentAgent?.agentid);

    const {
      data: properties = {
        Data: [],
        TotalRecords: 0,
        PageNumber: pageNumber ?? DEFAULT_PAGE,
        PageSize: pageSize ?? DEFAULT_SIZE,
        TotalPages: 1,
      },
      isLoading,
      refetch: isRefetch,
    } = useGetAgencyPropertiesQuery(queryArgs, {
      skip: shouldSkip,
      refetchOnMountOrArgChange: true,
    });

    // Optional: if you want to hard-refetch on scope flips:
    // useEffect(() => {
    //   if (!shouldSkip) refetch();
    // }, [scope, refetch, shouldSkip]);

    const {
      data: agentLeads = [],
      isLoading: LeadsLoading,
      refetch: refetchLeads,
    } = useGetLeadsQuery(
      { agencyId: currentAgent?.agencyid, agentId: currentAgent?.agentid },
      { skip: !currentAgent }
    );

    return (
      <PropertiesData.Provider value={{ properties, isLoading, isRefetch }}>
        <LeadsData.Provider
          value={{ agentLeads, LeadsLoading, refetch: refetchLeads }}
        >
          {children}
        </LeadsData.Provider>
      </PropertiesData.Provider>
    );
  };

  return (
    <Suspense
      fallback={
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      <DataProvider>{children}</DataProvider>
    </Suspense>
  );
};

export const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <FullLayout />
      </RequireAuth>
    ),
    errorElement: <div>Page Not Found</div>,
    children: [
      {
        path: "/",
        element: (
          <SuspenseLayout>
            <Dashboard />
          </SuspenseLayout>
        ),
      },
      {
        path: PROPERTY_ROUTE,
        element: (
          <SuspenseLayout>
            <Properties />
          </SuspenseLayout>
        ),
      },
      {
        path: ADD_PROPERTY_ROUTE,
        element: (
          <RequirePermission requiredPermission="addProperty">
            <SuspenseLayout>
              <AddProperty />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: EDIT_PROPERTY_ROUTE + ":propertyId",
        element: (
          <RequirePermission requiredPermission="updateProperty">
            <SuspenseLayout>
              <EditProperty />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: PROFILE_ROUTE,
        element: (
          <SuspenseLayout>
            <Profile />
          </SuspenseLayout>
        ),
      },
      {
        path: ADD_AGENT_ROUTE,
        element: (
          <RequirePermission requiredPermission="addstaff">
            <SuspenseLayout>
              <AddAgent />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: TASKS_ROUTE,
        element: (
          <SuspenseLayout>
            <Tasks />
          </SuspenseLayout>
        ),
      },
       {
        path: INTEGRATION_ROUTE,
        element: (
          <SuspenseLayout>
            <Integration />
          </SuspenseLayout>
        ),
      },
      // Meta redirects back to `/agent-whatsapp?wa=<result>` after the embedded
      // signup, so the Agent WhatsApp page is also reachable on that path.
      {
        path: AGENT_WHATSAPP_ROUTE,
        element: (
          <SuspenseLayout>
            <Integration />
          </SuspenseLayout>
        ),
      },
      {
        path: LEADS_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewlead">
            <SuspenseLayout>
              <Leads />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: ADD_LEAD_ROUTE,
        element: (
          <RequirePermission requiredPermission="addLead">
            <SuspenseLayout>
              <AddLead />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: LEADS_POOL_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewpool">
            <SuspenseLayout>
              <LeadPool />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: EDIT_LEAD_ROUTE + ":leadId",
        element: (
          <RequirePermission requiredPermission="updateLead">
            <SuspenseLayout>
              <EditLead />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: LEADS_ROUTE + VIEW_ROUTE + APPOINTMENT_ROUTE,
        element: (
          <RequirePermission requiredPermission="updateLead">
            <SuspenseLayout>
              <ViewingArranged />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: LEADS_ROUTE + VIEW_ROUTE + VIEWING_DONE_ROUTE,
        element: (
          <RequirePermission requiredPermission="updateLead">
            <SuspenseLayout>
              <ViewingDone />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: LEADS_ROUTE + VIEW_ROUTE + DEAL_DEAL_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewAccounts">
            <SuspenseLayout>
              <Deal />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: LEADS_ROUTE + VIEW_ROUTE + VIEW_DETAILS_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewlead">
            <SuspenseLayout>
              <LeadDetails />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: HISTORY_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewlead">
            <SuspenseLayout>
              <History />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: All_LEADS_ROUTE,
        element: (
          // <RequirePermission requiredPermission="viewalllead">
            <SuspenseLayout>
              <AllLead />
            </SuspenseLayout>
          // </RequirePermission>
        ),
      },

      {
        path: TODAY_LEADS_ROUTE,
        element: (
          <RequirePermission requiredPermission="todayleads">
            <SuspenseLayout>
              <TodayLeads />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },

      {
        path: CONTACTS_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewContacts">
            <SuspenseLayout>
              <Contacts />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },

      {
        path: ACCOUNT_DEALS_ROUTE,
        element: (
          <RequirePermission requiredPermission="viewAccounts">
            <SuspenseLayout>
              <DealsPdf />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: TRANSACTION_DETAILS_ROUTE + "/:dealId",
        element: (
          <RequirePermission requiredPermission="viewAccounts">
            <SuspenseLayout>
              <TransactionDetails />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
      {
        path: ACCOUNT_ADD_TRANSACTION_ROUTE + ":dealId" + "/:transactionId",
        element: (
          <RequirePermission requiredPermission="viewAccounts">
            <SuspenseLayout>
              <AddTransaction />
            </SuspenseLayout>
          </RequirePermission>
        ),
      },
    ],
  },
]);
